import { AdminActionsForCompanyQuery } from '#app/__generated__/relay/AdminActionsForCompanyQuery.graphql.js';
import { Link } from '#app/components/Link/index.js';
import { ImpersonateUser } from '#app/features/admin/components/ImpersonateUser/ImpersonateUser.js';
import { formatName } from '#app/utilities/formatName.js';
import { CloseIcon } from '@contra/icons/icons';
import { graphql, useLazyLoadQuery } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { Suspense, useState } from 'react';
import { css, styled } from 'styled-components';

const CloseButton = styled.button`
  position: absolute;
  top: -24px;
  right: 6px;
`;

const Container = styled.div`
  ${({ theme }) => css`
    padding: 16px;
    position: fixed;
    bottom: 88px;
    right: 16px;

    background-color: white;
    border-radius: 8px;

    box-shadow: ${theme.boxShadow.toast};
    z-index: ${theme.zIndices.tooltip};

    & > a {
      color: ${theme.colorsV2.black};
      font-weight: 600;
      margin-bottom: 16px;
      padding-bottom: 16px;
      border-bottom: 1px solid ${theme.colorsV2.gray30};
    }

    > * + * {
      margin-bottom: 12px;
      display: block;

      &:last-child {
        margin-bottom: 0px;
      }
    }
  `}
`;

const UserRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 250px;

    & > :first-child {
      color: ${theme.colorsV2.black};
      font-weight: 600;
    }

    & > :not(:first-child) {
      align-self: flex-end;
    }

    &:not(:last-child) {
      margin-bottom: 16px;
      padding-bottom: 16px;
      border-bottom: 1px solid ${theme.colorsV2.gray30};
    }
  `}
`;

type Props = {
  readonly companySlug: string;
  readonly disablePositioning?: boolean;
};

const InternalAdminActionsForCompany = ({ companySlug }: Props) => {
  const data = useLazyLoadQuery<AdminActionsForCompanyQuery>(
    graphql`
      query AdminActionsForCompanyQuery($slug: String!) {
        organizationBySlug(slug: $slug) {
          id
          hubspotCompanyUrl
          publicMembers {
            edges {
              node {
                userProfile {
                  id
                  firstName
                  lastName
                  displayUsername
                  hubspotContactUrl
                }
              }
            }
          }
        }
      }
    `,
    {
      slug: companySlug,
    },
  );

  if (!data.organizationBySlug) {
    return null;
  }

  const { hubspotCompanyUrl, publicMembers } = data.organizationBySlug;

  return (
    <>
      {hubspotCompanyUrl ? (
        <a
          href={hubspotCompanyUrl}
          rel="noreferrer"
          target="_blank"
        >
          🏭 Hubspot company page
        </a>
      ) : null}

      {publicMembers.edges.map(({ node: { userProfile } }) => {
        const fullName = formatName(userProfile, 'fullName');

        return (
          <UserRow key={userProfile.id}>
            <Link
              target="_blank"
              to={getRoute('/@username', {
                args: { username: userProfile.displayUsername },
              })}
            >
              {fullName}
            </Link>
            <ImpersonateUser userProfileId={userProfile.id} />
            {userProfile.hubspotContactUrl ? (
              <a
                href={userProfile.hubspotContactUrl ?? ''}
                rel="noreferrer"
                target="_blank"
              >
                🛂 Hubspot Contact
              </a>
            ) : null}
          </UserRow>
        );
      })}
    </>
  );
};

export const AdminActionsForCompany = ({ companySlug }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Container>
      {isOpen ? (
        <>
          <CloseButton
            onClick={() => setIsOpen(false)}
            type="button"
          >
            <CloseIcon />
          </CloseButton>

          <Suspense>
            <InternalAdminActionsForCompany companySlug={companySlug} />
          </Suspense>
        </>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          type="button"
        >
          Show
        </button>
      )}
    </Container>
  );
};
